/**
 * Fixtures for the CRM modules that `salescrmapi` does not serve yet
 * (`docs/API.md` §5–§13). Served only through `mockFallback`, only on a 501.
 *
 * Company names, owners and deal values are kept consistent with
 * `leads.fixture.js` and `contacts.fixture.js` so a record followed across
 * screens — lead → account → opportunity → quotation — tells one coherent story
 * rather than three unrelated ones. That coherence is the point: a demo where
 * the account page shows a different owner than the lead page teaches reviewers
 * to distrust the screens.
 */

/* The accounts fixture that stood here is gone. §5 is implemented against a
   real crm.account row shape (lifecycle_stage, health.band, segment as free
   text — not type/tier/health as fixed strings), so a fixture built against the
   old made-up shape was a trap waiting for whoever next turned mocks on.
   `accountId`/`relatedId` below still reference its old `AC-00xx` ids for their
   own internal story — opportunities and this file's own activities fixture are
   still mock-only screens, and those ids were never actually looked up against
   this array at render time, only carried alongside a plain-text `account` /
   `related` name. */

/** iam-visible pipeline stages, in order. Mirrors `md.opportunity_stage`. */
export const stages = [
  { key: 'qualification', label: 'Qualification', probability: 10, tone: 'sky' },
  { key: 'discovery', label: 'Discovery', probability: 25, tone: 'violet' },
  { key: 'proposal', label: 'Proposal', probability: 50, tone: 'amber' },
  { key: 'negotiation', label: 'Negotiation', probability: 75, tone: 'teal' },
  { key: 'closed_won', label: 'Closed Won', probability: 100, tone: 'emerald' },
  { key: 'closed_lost', label: 'Closed Lost', probability: 0, tone: 'rose' },
];

export const opportunities = [
  { id: 'OP-0231', name: 'Northwind — Analytics Platform renewal', account: 'Northwind Analytics', accountId: 'AC-0041', stage: 'negotiation', amount: 185000, currency: 'USD', probability: 75, closeDate: '2026-09-28', owner: 'Aisha Kapoor', age: 42, nextStep: 'Legal review of MSA', competitor: 'Databricks', source: 'LinkedIn' },
  { id: 'OP-0228', name: 'Helix Robotics — Fleet telemetry', account: 'Helix Robotics', accountId: 'AC-0038', stage: 'proposal', amount: 96000, currency: 'EUR', probability: 50, closeDate: '2026-10-15', owner: 'Marek Kowalski', age: 28, nextStep: 'Send revised quotation', competitor: null, source: 'Exhibition' },
  { id: 'OP-0224', name: 'Meridian Health — Compliance suite', account: 'Meridian Health', accountId: 'AC-0035', stage: 'discovery', amount: 210000, currency: 'USD', probability: 25, closeDate: '2026-11-30', owner: 'Aisha Kapoor', age: 61, nextStep: 'Security questionnaire', competitor: 'Epic', source: 'Referral' },
  { id: 'OP-0219', name: 'Vertex Media — Audience insights', account: 'Vertex Media', accountId: 'AC-0031', stage: 'closed_won', amount: 64000, currency: 'GBP', probability: 100, closeDate: '2026-08-29', owner: 'Sara Brandt', age: 35, nextStep: 'Kick-off scheduled', competitor: null, source: 'Web' },
  { id: 'OP-0215', name: 'Cobalt Energy — Grid analytics pilot', account: 'Cobalt Energy', accountId: 'AC-0029', stage: 'qualification', amount: 132000, currency: 'EUR', probability: 10, closeDate: '2026-12-18', owner: 'Unassigned', age: 12, nextStep: 'Assign an owner', competitor: 'Siemens', source: 'Advertisement' },
  { id: 'OP-0210', name: 'Kyoto Systems — Platform expansion', account: 'Kyoto Systems', accountId: 'AC-0021', stage: 'proposal', amount: 144000, currency: 'JPY', probability: 50, closeDate: '2026-10-02', owner: 'Jonas Weber', age: 47, nextStep: 'Executive sponsor call', competitor: null, source: 'Partner' },
  { id: 'OP-0206', name: 'Danube Steel — Process monitoring', account: 'Danube Steel', accountId: 'AC-0018', stage: 'discovery', amount: 78000, currency: 'EUR', probability: 25, closeDate: '2026-11-08', owner: 'Marek Kowalski', age: 22, nextStep: 'Site visit', competitor: 'SAP', source: 'Exhibition' },
  { id: 'OP-0198', name: 'Solaris Retail — Store analytics', account: 'Solaris Retail', accountId: 'AC-0024', stage: 'closed_lost', amount: 58000, currency: 'EUR', probability: 0, closeDate: '2026-08-12', owner: 'Diego Ramos', age: 89, nextStep: null, lostReason: 'Budget withdrawn', competitor: 'Tableau', source: 'Google' },
];

/* The `quotations`/`quotationLines` fixtures that stood here are gone — §7 is
   implemented against a real crm.quotation row shape now (see
   api/quotations.api.js, api/approvals.api.js), so there is nothing left to
   fall back to on a 501. The `activities`/`followUps`/`feed` fixtures that
   stood after them are gone for the same reason: §8 activities, follow-ups
   (api/followups.api.js) and the feed (activities.api.js's listActivities,
   recent-first — there is no separate feed endpoint) are all real now. */

/* -------------------------------------------------------------------------- */
/* Dashboards                                                                  */
/* -------------------------------------------------------------------------- */

export const dashboards = {
  rep: {
    /* The hero band on dashboard-rep.html — quota attainment with the gap and
       the days left, which is the one question the greeting is answering. */
    hero: {
      attained: 720000, target: 1000000, pct: 72, daysLeft: 28, currency: 'USD',
      territory: 'Mid-Market — West',
      closestWin: { name: 'Northwind Analytics', amount: 148000 },
      newLeads: 24,
    },
    kpis: [
      { key: 'leads', label: 'My open leads', value: 48, delta: +12, tone: 'brand', meta: '11 hot · 19 warm · 18 cold' },
      { key: 'pipeline', label: 'Open pipeline', value: 1420000, format: 'money', delta: +8, tone: 'sky', meta: '17 deals · weighted $486K' },
      { key: 'followups', label: 'Follow-ups today', value: 18, suffix: ' / 25', delta: -3, deltaSuffix: '', tone: 'amber', meta: '7 overdue' },
      { key: 'won', label: 'Deals won (QTD)', value: 9, delta: +2, deltaSuffix: '', tone: 'emerald', meta: 'Win rate 34% · avg $80K' },
    ],
    stageMix: [
      { label: 'Qualification', value: 132000 },
      { label: 'Discovery', value: 288000 },
      { label: 'Proposal', value: 240000 },
      { label: 'Negotiation', value: 185000 },
    ],
    /* Score ≥ 80, mine, untouched for 48h — the list the prototype calls "hot
       leads to work". Kept name-for-name with leads.fixture.js so following one
       through to the lead detail screen shows the same person. */
    hotLeads: [
      { id: 'LD-00184', name: 'Sofia Marchetti', title: 'Head of Operations', company: 'Northwind Analytics', source: 'LinkedIn', score: 92, status: 'Qualified', tone: 'emerald', value: 185000, next: 'Today 11:00' },
      { id: 'LD-00179', name: 'Ravi Deshmukh', title: 'VP Engineering', company: 'Helix Robotics', source: 'Exhibition', score: 88, status: 'Contacted', tone: 'sky', value: 96000, next: 'Today 14:30' },
      { id: 'LD-00171', name: 'Grace Adeyemi', title: 'Director of IT', company: 'Meridian Health', source: 'Referral', score: 86, status: 'Nurturing', tone: 'violet', value: 210000, next: 'Overdue 2d', overdue: true },
      { id: 'LD-00166', name: 'Tomas Berg', title: 'Head of Data', company: 'Vertex Media', source: 'Website', score: 83, status: 'Qualified', tone: 'emerald', value: 64000, next: 'Tomorrow' },
      { id: 'LD-00158', name: 'Lena Okonjo', title: 'COO', company: 'Cobalt Energy', source: 'Ad campaign', score: 81, status: 'New', tone: 'brand', value: 132000, next: '03 Sep' },
    ],
    followUpSnapshot: {
      dueToday: 18, overdue: 7, upcoming: 24, completed: 12, completionRate: 83,
    },
    /* §12 scoring exists; the assistant does not. These read as suggestions a
       human accepts or dismisses, which is the only honest framing for a panel
       whose recommendations nothing currently generates. */
    assistant: [
      {
        id: 'AI-1', kind: 'risk', badge: 'Deal at risk', icon: 'bi-exclamation-triangle-fill', tone: 'rose',
        title: 'Vertex Media has had no decision-maker contact for 14 days.',
        detail: 'Signals: single-threaded, budget unconfirmed, competitor mentioned twice.',
        action: 'Draft re-engage email',
      },
      {
        id: 'AI-2', kind: 'signal', badge: 'Buying signal', icon: 'bi-lightning-fill', tone: 'emerald',
        title: "Northwind's CFO opened the pricing page 5× in 3 days.",
        detail: 'Recommended next step: propose a commercial review this week.',
        action: 'Book meeting',
      },
    ],
    activityTrend: [
      { label: 'Mon', calls: 6, emails: 11, meetings: 2 },
      { label: 'Tue', calls: 4, emails: 9, meetings: 3 },
      { label: 'Wed', calls: 8, emails: 14, meetings: 1 },
      { label: 'Thu', calls: 5, emails: 7, meetings: 2 },
      { label: 'Fri', calls: 3, emails: 5, meetings: 1 },
    ],
    activityTotals: { calls: 64, meetings: 12, emails: 89 },
  },

  manager: {
    /* `reps` counts the rows below, not a larger team the table does not
       show — the header said 8 while six were listed, and the card footer
       computed 72% from those six under a KPI tile reading 68%. */
    team: { name: 'EMEA — Enterprise team', reps: 6, period: 'Q3 FY26' },
    approvalAlert: {
      count: 6,
      detail: '4 discount requests (10–20% band) · 1 non-standard payment term · 1 credit exception.',
      oldestHours: 19,
      slaHours: 24,
    },
    kpis: [
      { key: 'pipeline', label: 'Team pipeline', value: 5840000, format: 'money', delta: +18, tone: 'brand', meta: 'Coverage 3.2× against target' },
      { key: 'teamQuota', label: 'Quota attainment', value: 72, suffix: '%', delta: +6, deltaSuffix: 'pt', tone: 'emerald', meter: 72 },
      { key: 'winRate', label: 'Win rate', value: 31.4, suffix: '%', delta: 0, tone: 'sky', meta: 'Avg cycle 54 days' },
      { key: 'slipping', label: 'Stalled deals', value: 14, delta: +4, deltaSuffix: '', tone: 'rose', meta: '$1.1M idle > 21 days' },
    ],
    /* Closed / commit / best case stack against a target line, exactly as the
       prototype's revChart does — a stacked bar answers "will we make the
       number", which a pair of lines does not. */
    revenueTrend: [
      { label: 'Apr', closed: 720000, commit: 0, bestCase: 0, target: 850000 },
      { label: 'May', closed: 810000, commit: 0, bestCase: 0, target: 850000 },
      { label: 'Jun', closed: 905000, commit: 0, bestCase: 0, target: 900000 },
      { label: 'Jul', closed: 860000, commit: 0, bestCase: 0, target: 900000 },
      { label: 'Aug', closed: 940000, commit: 0, bestCase: 0, target: 950000 },
      { label: 'Sep', closed: 410000, commit: 320000, bestCase: 280000, target: 950000 },
    ],
    funnel: [
      { label: 'Leads captured', value: 2840 },
      { label: 'Contacted', value: 1920 },
      { label: 'Qualified', value: 980 },
      { label: 'Proposal sent', value: 462 },
      { label: 'Negotiation', value: 318 },
      { label: 'Closed won', value: 278 },
    ],
    funnelStats: { leadToWon: 9.8, responseMinutes: 42 },
    sourceMix: [
      { label: 'Website', value: 34 },
      { label: 'LinkedIn', value: 22 },
      { label: 'Referral', value: 15 },
      { label: 'Exhibition', value: 11 },
      { label: 'Ad campaign', value: 10 },
      { label: 'Partner', value: 8 },
    ],
    reps: [
      { name: 'Aisha Kapoor', territory: 'Mid-Market — West', quota: 250000, achieved: 232000, pipeline: 610000, winRate: 38, activities: '96 calls · 18 mtgs' },
      { name: 'Jonas Weber', territory: 'DACH — Enterprise', quota: 300000, achieved: 258000, pipeline: 980000, winRate: 34, activities: '71 calls · 22 mtgs' },
      { name: 'Chloe Dubois', territory: 'France & Benelux', quota: 250000, achieved: 196000, pipeline: 520000, winRate: 31, activities: '88 calls · 14 mtgs' },
      { name: 'Marek Kowalski', territory: 'CEE — Enterprise', quota: 280000, achieved: 171000, pipeline: 760000, winRate: 22, activities: '62 calls · 9 mtgs' },
      { name: 'Sara Brandt', territory: 'Nordics', quota: 220000, achieved: 124000, pipeline: 352000, winRate: 26, activities: '41 calls · 11 mtgs' },
      { name: 'Diego Ramos', territory: 'Iberia', quota: 200000, achieved: 102000, pipeline: 410000, winRate: 24, activities: '57 calls · 12 mtgs' },
    ],
    /* Three of six: the strip above counts the queue, this lists the oldest. */
    approvalCount: 6,
    approvals: [
      { id: 'QT-2026-0418', title: 'Discount 18% — Helix Robotics', requester: 'Marek Kowalski', amount: 96000, waited: '19h ago' },
      { id: 'QT-2026-0419', title: 'Net-90 payment terms — Cobalt Energy', requester: 'Sara Brandt', amount: 132000, waited: '6h ago', note: 'Non-standard' },
      { id: 'QT-2026-0421', title: 'Discount 14% — Meridian Health', requester: 'Aisha Kapoor', amount: 210000, waited: '3h ago' },
    ],
    approvalRule: 'Rule: ≤10% rep · 10–20% manager · >20% director. Configurable in Administration → Workflow.',
    coaching: [
      { tone: 'rose', name: 'Marek Kowalski', note: '41% of deals stall at Proposal. Discovery notes missing decision-maker on 6 of 9 open deals.' },
      { tone: 'amber', name: 'Sara Brandt', note: 'Call volume is 38% below team median while pipeline coverage sits at 1.6×.' },
      { tone: 'emerald', name: 'Aisha Kapoor', note: 'Best-in-team follow-up SLA (12 min). Worth sharing her call-prep playbook.' },
    ],
  },

  exec: {
    hero: { closed: 18400000, plan: 22000000, pct: 84, yoy: 23.6, priorYear: 14900000, daysLeft: 28, currency: 'USD' },
    kpis: [
      { key: 'pipeline', label: 'Total pipeline', value: 62100000, format: 'money', tone: 'brand', meta: '2.8× coverage' },
      { key: 'winRate', label: 'Win rate', value: 33.8, suffix: '%', delta: +2.1, deltaSuffix: 'pt', tone: 'emerald' },
      { key: 'customers', label: 'New customers', value: 214, tone: 'sky', meta: 'CAC $4.2K · LTV $86K' },
      { key: 'cycle', label: 'Avg sales cycle', value: 58, suffix: 'd', delta: -6, deltaSuffix: ' days', tone: 'amber' },
    ],
    /* Millions, not units: the chart is read at a glance from across a room and
       a twelve-point series of nine-digit numbers is not. */
    revenueTrend: [
      { label: 'Oct', actual: 4.1, forecast: 4.0, plan: 4.2 },
      { label: 'Nov', actual: 4.6, forecast: 4.4, plan: 4.5 },
      { label: 'Dec', actual: 6.2, forecast: 6.0, plan: 5.8 },
      { label: 'Jan', actual: 4.4, forecast: 4.5, plan: 4.6 },
      { label: 'Feb', actual: 4.9, forecast: 4.7, plan: 4.8 },
      { label: 'Mar', actual: 6.8, forecast: 6.5, plan: 6.4 },
      { label: 'Apr', actual: 5.2, forecast: 5.4, plan: 5.3 },
      { label: 'May', actual: 5.7, forecast: 5.5, plan: 5.6 },
      { label: 'Jun', actual: 7.4, forecast: 7.0, plan: 7.1 },
      { label: 'Jul', actual: 5.9, forecast: 6.1, plan: 6.0 },
      { label: 'Aug', actual: 6.3, forecast: 6.0, plan: 6.2 },
      { label: 'Sep', actual: 6.2, forecast: 6.6, plan: 6.4 },
    ],
    trendFacts: [
      { label: 'Forecast accuracy', value: '94.2%' },
      { label: 'Commit achieved', value: '7 of 8 quarters' },
      { label: 'Pipeline velocity', value: '$412K / day' },
    ],
    forecastCategories: [
      { label: 'Closed', value: 18400000 },
      { label: 'Commit', value: 4100000 },
      { label: 'Best case', value: 6800000 },
      { label: 'Pipeline', value: 32800000 },
    ],
    projectedClose: { amount: 23900000, planPct: 109, basis: 'Risk-adjusted using 3-year seasonality' },
    regions: [
      { label: 'North America', actual: 7800000, target: 8500000 },
      { label: 'EMEA', actual: 6100000, target: 6400000 },
      { label: 'APAC', actual: 3200000, target: 4600000 },
      { label: 'LATAM', actual: 1300000, target: 1400000 },
    ],
    byProduct: [
      { label: 'Platform', value: 8200000 },
      { label: 'Analytics Pro', value: 4600000 },
      { label: 'Field Suite', value: 2900000 },
      { label: 'Integrations', value: 1700000 },
      { label: 'Support Plus', value: 1000000 },
    ],
    health: [
      { label: 'Healthy', value: 61 },
      { label: 'Good', value: 22 },
      { label: 'At risk', value: 12 },
      { label: 'Critical', value: 5 },
    ],
    healthAccounts: 1482,
    topDeals: [
      { id: 'OP-0401', name: 'Global rollout — Northwind Group', sub: 'Enterprise pipeline · Jonas Weber', region: 'DACH', stage: 'Negotiation', tone: 'violet', value: 2400000, probability: 70, close: '18 Sep' },
      { id: 'OP-0398', name: 'Platform renewal — Meridian Health', sub: 'Renewal pipeline · Aisha Kapoor', region: 'NA West', stage: 'Procurement', tone: 'sky', value: 1850000, probability: 65, close: '30 Sep' },
      { id: 'OP-0392', name: 'Fleet analytics — Aurora Logistics', sub: 'New business · Chloe Dubois', region: 'France', stage: 'Proposal', tone: 'amber', value: 1200000, probability: 45, close: '11 Oct' },
      { id: 'OP-0388', name: 'Automation suite — Helix Robotics', sub: 'Enterprise pipeline · Marek Kowalski', region: 'CEE', stage: 'Security review', tone: 'brand', value: 980000, probability: 50, close: '24 Oct' },
      { id: 'OP-0381', name: 'Grid modernisation — Cobalt Energy', sub: 'New business · Sara Brandt', region: 'Nordics', stage: 'Discovery', tone: 'neutral', value: 860000, probability: 25, close: '14 Nov' },
    ],
    signals: [
      { tone: 'emerald', title: 'Nordics is the fastest-improving region', detail: '+41% pipeline QoQ on the back of the Q2 exhibition programme. Consider reallocating two headcount from Iberia.' },
      { tone: 'rose', title: 'Enterprise cycle is stretching', detail: 'Security review adds a median 19 days. 8 deals worth $6.2M currently sit in that stage beyond SLA.' },
      { tone: 'amber', title: 'Discount pressure rising', detail: 'Average approved discount moved 9.2% → 12.6% in two quarters, mostly against one competitor in mid-market.' },
      { tone: 'sky', title: 'Retention is holding', detail: 'Gross retention 94%, net 112%. 38 contracts worth $8.1M renew inside 90 days — renewal motion is on track.' },
    ],
  },

  admin: {
    organization: { name: 'Entiovi Group Holdings', businessUnits: 4, regions: 3 },
    kpis: [
      { key: 'users', label: 'Active users', value: 312, delta: +9, deltaSuffix: '', tone: 'brand', meta: '287 licensed · 25 read-only · 6 suspended' },
      { key: 'records', label: 'CRM records', value: '2.8M', delta: +4.2, tone: 'violet', meta: 'Leads 940K · Accounts 148K · Deals 62K' },
      { key: 'failed', label: 'Security events (24h)', value: 11, delta: +3, deltaSuffix: '', tone: 'rose', meta: '8 failed logins · 2 new devices · 1 export' },
      { key: 'integrations', label: 'Integrations healthy', value: 14, suffix: ' / 15', delta: 0, tone: 'amber', meta: '1 token expiring' },
    ],
    /* The prototype's banner reads "All systems operational" unconditionally.
       Here it is derived from `services` below, which reports what is actually
       wired up in this build — a health board that shows green for something
       never connected is worse than no health board. */
    platform: [
      { label: 'Uptime 30d', value: '99.98%' },
      { label: 'API p95', value: '118ms' },
      { label: 'Sev-1 open', value: '0' },
    ],
    dataQuality: [
      { issue: 'Duplicate accounts detected', module: 'Accounts', records: 1284, share: 4.2, severity: 'High', tone: 'rose', action: 'Merge' },
      { issue: 'Leads with no owner', module: 'Leads', records: 612, share: 0.7, severity: 'High', tone: 'rose', action: 'Assign' },
      { issue: 'Opportunities with no next activity', module: 'Opportunities', records: 438, share: 7.1, severity: 'Medium', tone: 'amber', action: 'Notify' },
      { issue: 'Contacts missing email', module: 'Contacts', records: 2907, share: 3.6, severity: 'Medium', tone: 'amber', action: 'Enrich' },
      { issue: 'Invalid phone format', module: 'Contacts', records: 1146, share: 1.4, severity: 'Low', tone: 'sky', action: 'Normalise' },
      { issue: 'Stale opportunities (>90d)', module: 'Opportunities', records: 221, share: 3.6, severity: 'Low', tone: 'sky', action: 'Review' },
    ],
    adoption: [
      { label: 'W1', active: 198, licensed: 276 },
      { label: 'W2', active: 206, licensed: 278 },
      { label: 'W3', active: 215, licensed: 281 },
      { label: 'W4', active: 222, licensed: 283 },
      { label: 'W5', active: 228, licensed: 285 },
      { label: 'W6', active: 234, licensed: 286 },
      { label: 'W7', active: 239, licensed: 287 },
      { label: 'W8', active: 241, licensed: 287 },
    ],
    adoptionStats: [
      { label: 'Daily active users', value: '241', share: '77%' },
      { label: 'MFA enrolled', value: '298', share: '96%', tone: 'emerald' },
      { label: 'Mobile app users', value: '164', share: '53%' },
      { label: 'Never logged in (30d)', value: '14', tone: 'rose' },
    ],
    /* What this build actually runs. Mail transport and background workers are
       not configured, and the boot log says so on every start. */
    services: [
      { component: 'API', status: 'Operational', detail: 'p95 118 ms', tone: 'emerald', icon: 'bi-hdd-network-fill' },
      { component: 'PostgreSQL', status: 'Operational', detail: '12 connections, 0 waiting', tone: 'emerald', icon: 'bi-database-fill' },
      { component: 'Object storage (MinIO)', status: 'Not configured', detail: 'Presigned upload URLs unavailable', tone: 'amber', icon: 'bi-box-seam-fill' },
      { component: 'Mail transport', status: 'Not configured', detail: 'Reset and invitation links go to the log', tone: 'rose', icon: 'bi-envelope-fill' },
      { component: 'Background workers', status: 'Not running', detail: 'Nothing drains the job queue', tone: 'rose', icon: 'bi-cpu-fill' },
      { component: 'Single sign-on', status: 'Local only', detail: 'One local identity provider enabled', tone: 'sky', icon: 'bi-shield-lock-fill' },
    ],
  },
};

/* -------------------------------------------------------------------------- */
/* Reports                                                                     */
/* -------------------------------------------------------------------------- */

export const reports = {
  forecast: [
    { period: 'Sep 2026', commit: 640000, bestCase: 380000, pipeline: 290000, closed: 249000, quota: 900000 },
    { period: 'Oct 2026', commit: 720000, bestCase: 410000, pipeline: 340000, closed: 0, quota: 900000 },
    { period: 'Nov 2026', commit: 620000, bestCase: 330000, pipeline: 480000, closed: 0, quota: 950000 },
  ],
  leadFunnel: [
    { stage: 'New', count: 412 },
    { stage: 'Working', count: 268 },
    { stage: 'Nurturing', count: 154 },
    { stage: 'Qualified', count: 96 },
    { stage: 'Converted', count: 41 },
  ],
  targets: [
    { owner: 'Aisha Kapoor', period: 'Q3 2026', quota: 420000, attained: 331000, pct: 79 },
    { owner: 'Marek Kowalski', period: 'Q3 2026', quota: 380000, attained: 244000, pct: 64 },
    { owner: 'Sara Brandt', period: 'Q3 2026', quota: 340000, attained: 318000, pct: 94 },
    { owner: 'Diego Ramos', period: 'Q3 2026', quota: 300000, attained: 148000, pct: 49 },
    { owner: 'Jonas Weber', period: 'Q3 2026', quota: 400000, attained: 289000, pct: 72 },
  ],
  territories: [
    { name: 'EMEA — Enterprise', owner: 'Daniel Okafor', accounts: 84, pipeline: 1840000, attainment: 74 },
    { name: 'Mid-Market — West', owner: 'Aisha Kapoor', accounts: 132, pipeline: 960000, attainment: 79 },
    { name: 'APAC', owner: 'Jonas Weber', accounts: 61, pipeline: 1120000, attainment: 72 },
    { name: 'Iberia', owner: 'Diego Ramos', accounts: 47, pipeline: 420000, attainment: 49 },
  ],
  campaigns: [
    { id: 'CP-021', name: 'Q3 LinkedIn ABM', type: 'Digital', status: 'Active', budget: 48000, spent: 31200, leads: 184, converted: 22, roi: 3.4 },
    { id: 'CP-018', name: 'Manufacturing Expo', type: 'Event', status: 'Completed', budget: 65000, spent: 65000, leads: 96, converted: 14, roi: 2.1 },
    { id: 'CP-015', name: 'Partner Referral', type: 'Referral', status: 'Active', budget: 20000, spent: 8400, leads: 61, converted: 19, roi: 6.8 },
    { id: 'CP-011', name: 'Search advertising', type: 'Digital', status: 'Paused', budget: 36000, spent: 29800, leads: 240, converted: 11, roi: 0.9 },
  ],
};

/* -------------------------------------------------------------------------- */
/* Administration (§12 audit, §14 automation, §2 security)                     */
/* -------------------------------------------------------------------------- */

export const auditTrail = [
  { id: 1, at: '2026-09-05T09:42:11Z', actor: 'Aisha Kapoor', action: 'stage_change', entity: 'opportunity', record: 'OP-0231', detail: 'Proposal → Negotiation', ip: '203.0.113.44', result: 'success' },
  { id: 2, at: '2026-09-05T09:10:02Z', actor: 'Marek Kowalski', action: 'send', entity: 'quotation', record: 'QT-2026-0139', detail: 'Emailed to r.deshmukh@helixrobotics.com', ip: '198.51.100.7', result: 'success' },
  { id: 3, at: '2026-09-05T08:31:55Z', actor: 'Priya Raghavan', action: 'permission_change', entity: 'role', record: 'inside-sales', detail: 'Added quotations:approve (team)', ip: '203.0.113.9', result: 'success' },
  { id: 4, at: '2026-09-05T08:04:18Z', actor: 'unknown', action: 'login_failed', entity: 'user', record: 'admin@entiovi.test', detail: 'bad_password', ip: '192.0.2.188', result: 'failure' },
  { id: 5, at: '2026-09-04T17:20:44Z', actor: 'Daniel Okafor', action: 'approve', entity: 'quotation', record: 'QT-2026-0128', detail: 'Discount 8% within limit', ip: '203.0.113.61', result: 'success' },
  { id: 6, at: '2026-09-04T15:58:03Z', actor: 'Priya Raghavan', action: 'export', entity: 'lead', record: '412 rows', detail: 'CSV export — Leads / All open', ip: '203.0.113.9', result: 'success' },
];

export const workflows = [
  { id: 'WF-07', name: 'Assign inbound web leads round-robin', trigger: 'Lead created', condition: 'source = Web', actions: 2, status: 'Active', runs: 1284, lastRun: '4 minutes ago' },
  { id: 'WF-05', name: 'Escalate overdue follow-ups to manager', trigger: 'Follow-up overdue > 2 days', condition: 'owner.team = any', actions: 3, status: 'Active', runs: 341, lastRun: '2 hours ago' },
  { id: 'WF-04', name: 'Notify on discount above 20%', trigger: 'Quotation submitted', condition: 'discount_pct > 20', actions: 1, status: 'Active', runs: 62, lastRun: 'Yesterday' },
  { id: 'WF-02', name: 'Birthday greeting to champions', trigger: 'Contact birthday', condition: 'role = Champion', actions: 1, status: 'Paused', runs: 118, lastRun: '11 days ago' },
];

export const scoringRules = [
  { id: 'SR-1', label: 'Email opened', field: 'opens', points: 2, cap: 20, enabled: true },
  { id: 'SR-2', label: 'Email replied', field: 'replies', points: 8, cap: 32, enabled: true },
  { id: 'SR-3', label: 'Website visit', field: 'visits', points: 1, cap: 18, enabled: true },
  { id: 'SR-4', label: 'Meeting booked', field: 'meetings', points: 25, cap: 50, enabled: true },
  { id: 'SR-5', label: 'Company size > 500', field: 'employees', points: 10, cap: 10, enabled: true },
];

export const securityPolicy = {
  password_min_length: 12,
  password_require_mixed: true,
  password_require_digit: true,
  password_require_symbol: true,
  password_history_depth: 5,
  password_max_age_days: null,
  max_failed_attempts: 5,
  lockout_minutes: 15,
  session_idle_minutes: 60,
  session_absolute_hours: 12,
  sso_only: false,
  audit_retention_years: 7,
  log_retention_days: 90,
};

/* -------------------------------------------------------------------------- */
/* Master data (§10)                                                           */
/* -------------------------------------------------------------------------- */

/* The master-data fixture that stood here is gone. §10 is implemented, so the
   screen reads the real catalogue — and a fixture whose shape no longer matched
   the endpoint was a trap waiting for whoever next turned the mocks on. */

/* The email-templates fixture that stood here is gone. §8's template library is
   implemented against a real crm.email_template row shape (folder, is_shared,
   owner, usage_count — not category/uses/openRate), so a fixture built against
   the old made-up shape was a trap waiting for whoever next turned mocks on. */
